/** Stable operation IDs map to the one advertised customer-facing task. */
export const MCP_OPERATION_NAMES: Readonly<Record<string, string>> = {
  list_email_designs: 'list_emails',
  get_email_design: 'get_email',
  create_email_design: 'create_email',
  edit_email_with_ai: 'edit_email',
  clone_email_design: 'clone_email',
  delete_email_design: 'delete_email',
  export_email_design: 'export_email',
  import_email_design: 'import_email',
  import_figma_design: 'import_email',
  get_email_status: 'get_email',
  preview_email_across_clients: 'test_email_rendering',
  list_inbox_placement_tests: 'get_inbox_placement_results',
  get_inbox_placement_test: 'get_inbox_placement_results',
  html_to_png: 'render_html_image',
  list_templates: 'search_templates',
  create_email_group: 'save_email_group',
  update_email_group: 'save_email_group',
  create_contact: 'save_contact',
  update_contact: 'save_contact',
  delete_contact: 'delete_contacts',
  batch_delete_contacts: 'delete_contacts',
  list_custom_fields: 'list_contact_fields',
  create_custom_field: 'create_contact_field',
  delete_custom_field: 'delete_contact_field',
  get_audience: 'list_audiences',
  duplicate_audience: 'create_audience',
  create_automation: 'save_automation',
  update_automation: 'save_automation',
  get_automation_analytics: 'list_automations',
  create_trigger: 'save_trigger',
  update_trigger: 'save_trigger',
  get_trigger: 'list_triggers',
  check_trigger_ready: 'check_trigger',
  validate_trigger_payload: 'check_trigger',
  get_analytics_overview: 'get_email_analytics',
  list_sends: 'get_email_analytics',
  get_send: 'get_email_analytics',
  get_event_analytics: 'get_email_analytics',
  get_automation_run: 'list_automation_runs',
  list_audience_runs: 'list_automation_runs',
  get_audience_run: 'list_automation_runs',
  pause_audience_run: 'control_audience_run',
  resume_audience_run: 'control_audience_run',
  cancel_audience_run: 'control_audience_run',
  list_trigger_instances: 'list_trigger_events',
  get_trigger_instance: 'list_trigger_events',
  cancel_send: 'control_email_send',
  pause_send: 'control_email_send',
  resume_send: 'control_email_send',
  run_data_command: 'query_brew_data',
  // Detail reads MCP does not list: the task that answers them instead.
  get_contact: 'search_contacts',
  list_contacts: 'search_contacts',
  get_domain: 'list_domains',
  get_email_group: 'list_email_groups',
  get_custom_field: 'list_contact_fields',
}

export function mcpToolNameForOperation(operation: string): string {
  return MCP_OPERATION_NAMES[operation] ?? operation
}

/**
 * HTTP route phrases that shared recovery text (error suggestions, schema
 * help) names, keyed `METHOD /v1/path`, mapped to the operation that serves
 * the route. MCP cannot call a route, so the rewrite replaces the phrase with
 * the advertised tool. Only exact phrases are rewritten; a detail read's
 * `?include=a,b` becomes the tool "with the <id> and include: ["a", "b"]".
 * Any other query string, or a route missing here, stays as written. Every
 * entry is checked against the registry's method/path and the built MCP
 * catalog, and every rewritten `include` against the tool's advertised input,
 * in `tests/mcp/descriptions-route-phrases.test.ts`.
 */
export const MCP_ROUTE_PHRASE_OPERATIONS: Readonly<Record<string, string>> = {
  'GET /v1/audiences': 'list_audiences',
  'GET /v1/audiences/{audienceId}': 'get_audience',
  'GET /v1/automations': 'list_automations',
  'GET /v1/automations/{automationId}': 'get_automation',
  'PATCH /v1/automations/{automationId}': 'update_automation',
  'DELETE /v1/automations/{automationId}': 'delete_automation',
  'GET /v1/automations/runs': 'list_automation_runs',
  'GET /v1/automations/runs/{automationRunId}': 'get_automation_run',
  'GET /v1/automations/audience-runs': 'list_audience_runs',
  'GET /v1/automations/audience-runs/{audienceRunId}': 'get_audience_run',
  'GET /v1/automations/triggers': 'list_triggers',
  'GET /v1/automations/triggers/{triggerEventId}': 'get_trigger',
  'DELETE /v1/automations/triggers/{triggerEventId}': 'delete_trigger',
  'GET /v1/automations/triggers/{triggerEventId}/readiness':
    'check_trigger_ready',
  'GET /v1/automations/trigger-instances': 'list_trigger_instances',
  'GET /v1/automations/trigger-instances/{triggerInstanceId}':
    'get_trigger_instance',
  'POST /v1/automations/triggers/{triggerEventId}/fire': 'fire_trigger_event',
  'POST /v1/content/image-uploads': 'create_image_upload',
  'POST /v1/content/add-image': 'add_image',
  'GET /v1/brands': 'list_brands',
  'GET /v1/brands/{brandId}': 'get_brand_status',
  'GET /v1/usage': 'get_usage',
  'GET /v1/emails': 'list_email_designs',
  'GET /v1/emails/{emailId}': 'get_email_design',
  'POST /v1/emails/audit': 'audit_email',
  'POST /v1/emails/{emailId}/client-previews': 'preview_email_across_clients',
  'GET /v1/email-groups': 'list_email_groups',
  'POST /v1/sends': 'send_email',
  'GET /v1/sends': 'list_sends',
  'GET /v1/sends/{sendId}': 'get_send',
  'POST /v1/contacts': 'create_contact',
  'PATCH /v1/contacts/{email}': 'update_contact',
  'POST /v1/contacts/validate': 'validate_contacts',
  'GET /v1/domains': 'list_domains',
  'GET /v1/fields': 'list_custom_fields',
  'POST /v1/fields': 'create_custom_field',
  'GET /v1/flows': 'list_flows',
  'GET /v1/templates': 'list_templates',
}

/**
 * `METHOD /v1/path` or `/api/v1/path`, plus any query string it carries. The
 * query stops before sentence punctuation (a comma only when a value follows
 * it, as in `include=a,b`), so a trailing period or clause stays prose.
 */
const ROUTE_PHRASE =
  /(`?)\b(GET|POST|PUT|PATCH|DELETE) \/(?:api\/)?v1(\/[A-Za-z0-9_{}/-]*)(\?(?:[^\s`'"),;.]|,(?=[a-z]))*)?(`?)/g

const INCLUDE_ONLY_QUERY = /^\?include=([a-z]+(?:,[a-z]+)*)$/

/**
 * Arguments a tool needs to do what the HTTP route does, where the path id
 * alone is not enough: a task tool selects the operation with a selector, and
 * `save_contact` only creates when told to.
 */
const MCP_ROUTE_PHRASE_ARGUMENTS: Readonly<Record<string, string>> = {
  'POST /v1/contacts': 'createIfMissing: true',
  'GET /v1/sends': 'report: {kind: "sends"}',
  'GET /v1/sends/{sendId}': 'report: {kind: "sends", sendId}',
  'GET /v1/automations/runs': 'run: {kind: "execution"}',
  'GET /v1/automations/runs/{automationRunId}':
    'run: {kind: "execution", automationRunId}',
  'GET /v1/automations/audience-runs': 'run: {kind: "audience"}',
  'GET /v1/automations/audience-runs/{audienceRunId}':
    'run: {kind: "audience", audienceRunId}',
}

/**
 * The advertised tool for one route phrase, split into the tool name and what
 * the call needs besides it, or null to leave the phrase as written.
 */
function routePhraseTool(
  method: string,
  path: string,
  query: string | undefined
): { tool: string; with?: string } | null {
  const route = `${method} /v1${path}`
  const operation = MCP_ROUTE_PHRASE_OPERATIONS[route]
  if (!operation) {
    return null
  }
  const tool = mcpToolNameForOperation(operation)
  // A detail route names its row by the last path id; list/get tools need it.
  const id = [...path.matchAll(/\{([A-Za-z]+)\}/g)].at(-1)?.[1]
  const selector = MCP_ROUTE_PHRASE_ARGUMENTS[route]
  const argument = selector ?? (id ? `the ${id}` : undefined)
  if (!query) {
    return argument ? { tool, with: argument } : { tool }
  }
  // A selector tool takes its query inside the selector; folding `include`
  // in is not checked against that branch, so leave the phrase as written.
  if (selector) {
    return null
  }
  const include = INCLUDE_ONLY_QUERY.exec(query)?.[1]
  if (!include) {
    return null
  }
  const values = include
    .split(',')
    .map((value) => `"${value}"`)
    .join(', ')
  return {
    tool,
    with: id ? `the ${id} and include: [${values}]` : `include: [${values}]`,
  }
}

/**
 * Rewrite only HTTP route phrases to the MCP tool that serves them. Safe on
 * text that interpolates customer data (warning messages quote addresses,
 * node labels and ids), because a bare snake_case word is left alone.
 */
export function rewriteMcpRoutePhrases(text: string): string {
  return text.replace(
    ROUTE_PHRASE,
    (
      phrase,
      open: string,
      method: string,
      path: string,
      query: string | undefined,
      close: string
    ) => {
      const target = routePhraseTool(method, path, query)
      if (!target) {
        return phrase
      }
      const rest = target.with ? ` with ${target.with}` : ''
      // A code-quoted route keeps only the tool name in code.
      return open && close
        ? `\`${target.tool}\`${rest}`
        : `${open}${target.tool}${rest}${close}`
    }
  )
}

/** Rewrite references without matching partial names or arbitrary prose. */
export function rewriteMcpToolReferences(text: string): string {
  return (
    rewriteMcpRoutePhrases(text)
      // MCP carries the HTTP header as the `idempotency_key` argument.
      .replace(/\bIdempotency-Key\b( header)?/g, 'idempotency_key')
      .replace(/\b[a-z][a-z0-9]*_[a-z0-9_]+\b/g, (name) =>
        mcpToolNameForOperation(name)
      )
  )
}
